import * as React from 'react';
import { createResident } from '@/api/residents';
import { getAllUnits } from '@/api/units';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';

import { useUser } from '@/hooks/v2/use-user';
import ProfessionalDialog, { DialogActionButtons } from '@/components/common/professional-dialog';
import { UserPlus as UserPlusIcon } from '@phosphor-icons/react';

interface CreateResidentDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  communityId: string | number;
}

interface ResidentFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  unit_id: string;
  community_role?: 'community_manager' | 'community_member';
  unit_role?: 'unit_owner' | 'unit_tenant' | 'unit_resident';
  send_invite?: boolean;
}

export default function CreateResidentDialog({ open, onClose, onCreated, communityId }: CreateResidentDialogProps) {
  const { community } = useUser();
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<ResidentFormData>({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      unit_id: '',
      community_role: 'community_member',
      unit_role: 'unit_resident',
      send_invite: true,
    },
  });

  const communityRole = watch('community_role');

  // Fetch units for the dropdown
  const { data: unitsData } = useQuery({
    queryKey: ['units', communityId],
    queryFn: async () => await getAllUnits(),
    enabled: Boolean(community?.id) && open,
  });

  const unitsList = React.useMemo(() => {
    if (!unitsData) return [] as any[];
    if (Array.isArray((unitsData as any).units)) return (unitsData as any).units as any[];
    if (Array.isArray(unitsData)) return unitsData as any[];
    return [] as any[];
  }, [unitsData]);

  const createResidentMutation = useMutation({
    mutationFn: async (data: ResidentFormData) => {
      // Enforce unit assignment unless community manager
      if (data.community_role !== 'community_manager' && !data.unit_id) {
        throw new Error('Unit is required for non-manager residents.');
      }
      return await createResident(community?.id, data);
    },
    onSuccess: () => {
      reset();
      onCreated();
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = (data: ResidentFormData) => {
    createResidentMutation.mutate(data);
  };

  return (
    <ProfessionalDialog
      actions={
        <DialogActionButtons
          confirmText={createResidentMutation.isPending ? 'Creating...' : 'Create User'}
          loading={createResidentMutation.isPending}
          onCancel={handleClose}
          onConfirm={handleSubmit(onSubmit)}
        />
      }
      autoHeight
      contentSx={{ pt: 2, px: 3, pb: 2 }}
      icon={<UserPlusIcon size={20} />}
      maxWidth="sm"
      onClose={handleClose}
      open={open}
      subtitle="Create a new user and optionally assign a unit"
      title="Add New User"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item md={6} xs={12}>
            <Controller
              control={control}
              name="first_name"
              render={({ field }) => (
                <TextField
                  {...field}
                  error={Boolean(errors.first_name)}
                  fullWidth
                  helperText={errors.first_name?.message}
                  label="First Name"
                  placeholder="First Name"
                />
              )}
              rules={{ required: 'First name is required' }}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <Controller
              control={control}
              name="last_name"
              render={({ field }) => (
                <TextField
                  {...field}
                  error={Boolean(errors.last_name)}
                  fullWidth
                  helperText={errors.last_name?.message}
                  label="Last Name"
                  placeholder="Last Name"
                />
              )}
              rules={{ required: 'Last name is required' }}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <Controller
              control={control}
              name="email"
              render={({ field }) => (
                <TextField
                  {...field}
                  error={Boolean(errors.email)}
                  fullWidth
                  helperText={errors.email?.message}
                  label="Email Address"
                  placeholder="Email Address"
                />
              )}
              rules={{
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              }}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <Controller
              control={control}
              name="phone"
              render={({ field }) => (
                <TextField
                  {...field}
                  error={Boolean(errors.phone)}
                  fullWidth
                  helperText={errors.phone?.message}
                  label="Phone Number"
                  placeholder="(123) 456-7890"
                />
              )}
            />
          </Grid>
          {/* Community Role Selection */}
          <Grid item md={6} xs={12}>
            <Controller
              control={control}
              name="community_role"
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Community Role</InputLabel>
                  <Select {...field} label="Community Role">
                  <MenuItem value="community_member">Community Member</MenuItem>
                    <MenuItem value="community_manager">Community Manager</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
          {/* Unit Assignment and Role */}
          <Grid item md={6} xs={12}>
            <Controller
              control={control}
              name="unit_id"
              render={({ field }) => (
                <FormControl error={Boolean(errors.unit_id)} fullWidth>
                  <InputLabel>Assign to Unit</InputLabel>
                  <Select {...field} disabled={communityRole === 'community_manager'} label="Assign to Unit">
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {unitsList.map((unit: any) => (
                      <MenuItem key={unit.id} value={unit.id.toString()}>
                        {unit.address}
                        {unit.unit_number ? ` #${unit.unit_number}` : ''}
                        {unit.current_residents_count ? ` (${unit.current_residents_count})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.unit_id?.message ?? (communityRole !== 'community_manager' ? 'Required for non-managers' : undefined)}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <Controller
              control={control}
              name="unit_role"
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Unit Role</InputLabel>
                  <Select {...field} disabled={!watch('unit_id')} label="Unit Role">
                    <MenuItem value="unit_owner">Unit Owner</MenuItem>
                    <MenuItem value="unit_tenant">Unit Tenant</MenuItem>
                    <MenuItem value="unit_resident">Unit Resident</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Grid>
          {/* Invite Toggle */}
          <Grid item xs={12}>
            <Controller
              control={control}
              name="send_invite"
              render={({ field }) => (
                <FormControlLabel control={<Checkbox {...field} checked={Boolean(field.value)} />} label="Send welcome email" />
              )}
            />
          </Grid>
        </Grid>
      </form>
    </ProfessionalDialog>
  );
}
